import { useState, type FormEvent } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { FiUser, FiMail, FiLock, FiCreditCard, FiCheck } from 'react-icons/fi';
import { useAuth } from '../../contexts/AuthContext';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { Label } from '../../components/ui/Label';
import { Alert } from '../../components/ui/Alert';
import { AuthShell } from '../../components/auth/AuthShell';
import { GoogleButton } from '../../components/auth/GoogleButton';
import { Turnstile } from '../../components/Turnstile';
import { validarCPFouCNPJ, formatarDocumento, ehCNPJ } from '@ponti/shared/utils';
import { useCamposCadastro, inputTypeDoCampo } from '../../hooks/useCamposCadastro';
import { salvarRedirecionamento } from '../../lib/redirectPosLogin';

export function CadastroPage() {
  const { signUp, signInWithGoogle } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const destino = (location.state as { de?: string } | null)?.de ?? '/';

  const [nome, setNome] = useState('');
  const [nomeSocial, setNomeSocial] = useState('');
  const [email, setEmail] = useState('');
  const [documento, setDocumento] = useState('');
  const [docErro, setDocErro] = useState<string | null>(null);
  const [senha, setSenha] = useState('');
  const [senhaConfirma, setSenhaConfirma] = useState('');
  const [erro, setErro] = useState<string | null>(null);
  const [sucesso, setSucesso] = useState(false);
  const [carregando, setCarregando] = useState(false);
  const [google, setGoogle] = useState(false);
  const [captchaToken, setCaptchaToken] = useState<string | null>(null);
  const camposCustom = useCamposCadastro();
  const [valoresCustom, setValoresCustom] = useState<Record<string, string>>({});

  function validarCampoDoc(valor: string): boolean {
    const digitos = valor.replace(/\D/g, '');
    if (digitos.length === 0) { setDocErro(null); return false; }
    if (!validarCPFouCNPJ(valor)) {
      setDocErro(digitos.length > 11 ? 'CNPJ inválido — confira os dígitos.' : 'CPF inválido — confira os dígitos.');
      return false;
    }
    setDocErro(null);
    return true;
  }

  function onChangeDoc(valor: string) {
    const formatado = formatarDocumento(valor);
    setDocumento(formatado);
    const n = formatado.replace(/\D/g, '').length;
    // Revalida quando completa CPF (11) ou CNPJ (14); limpa o erro enquanto edita.
    if (n === 11 || n === 14) validarCampoDoc(formatado);
    else setDocErro(null);
  }

  function validar(): string | null {
    if (nome.trim().length < 3) return 'Informe o nome ou a razão social.';
    if (!email.includes('@')) return 'E-mail inválido.';
    if (!documento.trim()) return 'Informe seu CPF ou CNPJ.';
    if (!validarCPFouCNPJ(documento)) return 'CPF/CNPJ inválido — confira os dígitos.';
    if (senha.length < 8) return 'A senha precisa ter no mínimo 8 caracteres.';
    if (senha !== senhaConfirma) return 'As senhas não conferem.';
    const campoFaltando = camposCustom.find((c) => c.obrigatorio && !(valoresCustom[c.id]?.trim()));
    if (campoFaltando) return `Preencha o campo "${campoFaltando.nome}".`;
    return null;
  }

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    const erroValidacao = validar();
    if (erroValidacao) {
      setErro(erroValidacao);
      return;
    }
    setErro(null);
    setCarregando(true);
    const digitos = documento.replace(/\D/g, '');
    const empresa = ehCNPJ(documento);
    const { error } = await signUp({
      nome: nome.trim(),
      nome_social: nomeSocial.trim() || undefined,
      email: email.trim(),
      cpf: empresa ? undefined : digitos,
      cnpj: empresa ? digitos : undefined,
      senha,
      captchaToken: captchaToken ?? undefined,
      campos_customizados: Object.keys(valoresCustom).length > 0 ? valoresCustom : undefined,
    });
    setCarregando(false);
    if (error) {
      setErro(error);
      return;
    }
    setSucesso(true);
  }

  async function cadastrarComGoogle() {
    setErro(null);
    setGoogle(true);
    // OAuth recarrega a página — guarda o destino para retomar após o cadastro.
    salvarRedirecionamento(destino);
    const { error } = await signInWithGoogle();
    if (error) {
      setErro(error);
      setGoogle(false);
    }
  }

  if (sucesso) {
    return (
      <AuthShell titulo="Cadastro concluído!">
        <div className="text-center">
          <div className="w-12 h-12 mx-auto bg-emerald-100 text-emerald-600 rounded-full flex items-center justify-center mb-4">
            <FiCheck size={24} />
          </div>
          <p className="text-sm text-slate-600 mb-6">
            Enviamos um e-mail de confirmação para <strong>{email}</strong>. Verifique sua caixa de entrada
            (e a pasta de spam) para ativar sua conta.
          </p>
          <Button onClick={() => navigate('/login')} fullWidth className="h-11">Ir para o login</Button>
        </div>
      </AuthShell>
    );
  }

  return (
    <AuthShell titulo="Criar conta" subtitulo="É rápido e gratuito">
      <GoogleButton onClick={cadastrarComGoogle} loading={google} texto="Cadastrar com Google" />

      <div className="flex items-center gap-3 py-3">
        <div className="flex-1 h-px bg-slate-200" />
        <span className="text-xs text-slate-400">ou com e-mail</span>
        <div className="flex-1 h-px bg-slate-200" />
      </div>

      <form onSubmit={handleSubmit} className="space-y-4">
        {erro && <Alert variante="erro">{erro}</Alert>}

        <div>
          <Label htmlFor="nome" obrigatorio>Nome / Razão social</Label>
          <div className="relative">
            <FiUser className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={16} />
            <Input id="nome" required autoComplete="name" value={nome} onChange={(e) => setNome(e.target.value)} className="pl-9" placeholder="João da Silva ou Empresa LTDA" />
          </div>
        </div>

        <div>
          <Label htmlFor="nome_social">Nome social</Label>
          <div className="relative">
            <FiUser className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={16} />
            <Input id="nome_social" autoComplete="nickname" value={nomeSocial} onChange={(e) => setNomeSocial(e.target.value)} className="pl-9" placeholder="Como você prefere ser chamado(a)" />
          </div>
          <p className="text-xs text-slate-500 mt-1">Opcional — Decreto 8.727/2016. Usaremos preferencialmente este nome no atendimento.</p>
        </div>

        <div>
          <Label htmlFor="email" obrigatorio>E-mail</Label>
          <div className="relative">
            <FiMail className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={16} />
            <Input id="email" type="email" required autoComplete="email" value={email} onChange={(e) => setEmail(e.target.value)} className="pl-9" placeholder="seu@email.com" />
          </div>
        </div>

        <div>
          <Label htmlFor="documento" obrigatorio>CPF / CNPJ</Label>
          <div className="relative">
            <FiCreditCard className={`absolute left-3 top-1/2 -translate-y-1/2 ${docErro ? 'text-red-400' : 'text-slate-400'}`} size={16} />
            <Input
              id="documento"
              inputMode="numeric"
              required
              invalido={!!docErro}
              value={documento}
              onChange={(e) => onChangeDoc(e.target.value)}
              onBlur={() => validarCampoDoc(documento)}
              className="pl-9"
              placeholder="CPF (pessoa) ou CNPJ (empresa)"
              maxLength={18}
            />
          </div>
          {docErro
            ? <p className="text-xs text-red-600 mt-1">{docErro}</p>
            : <p className="text-xs text-slate-500 mt-1">Pessoa física informe o CPF; empresa/fornecedor informe o CNPJ.</p>}
        </div>

        <div>
          <Label htmlFor="senha" obrigatorio>Senha</Label>
          <div className="relative">
            <FiLock className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={16} />
            <Input id="senha" type="password" required autoComplete="new-password" value={senha} onChange={(e) => setSenha(e.target.value)} className="pl-9" placeholder="Mínimo 8 caracteres" />
          </div>
        </div>

        <div>
          <Label htmlFor="senha2" obrigatorio>Confirmar senha</Label>
          <div className="relative">
            <FiLock className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={16} />
            <Input id="senha2" type="password" required autoComplete="new-password" value={senhaConfirma} onChange={(e) => setSenhaConfirma(e.target.value)} className="pl-9" placeholder="Repita a senha" />
          </div>
        </div>

        {camposCustom.map((campo) => (
          <div key={campo.id}>
            <Label htmlFor={`custom-${campo.id}`} obrigatorio={campo.obrigatorio}>{campo.nome}</Label>
            <Input
              id={`custom-${campo.id}`}
              type={inputTypeDoCampo(campo.tipo)}
              inputMode={campo.tipo === 'numero' ? 'numeric' : undefined}
              value={valoresCustom[campo.id] ?? ''}
              onChange={(e) => setValoresCustom((p) => ({ ...p, [campo.id]: e.target.value }))}
              required={campo.obrigatorio}
            />
          </div>
        ))}

        <Turnstile onToken={setCaptchaToken} />

        <Button type="submit" loading={carregando} fullWidth className="h-11">Criar conta</Button>

        <p className="text-xs text-slate-500 text-center">
          Ao criar uma conta você concorda com os Termos de Uso e a Política de Privacidade.
        </p>
      </form>

      <p className="text-center text-sm text-slate-600 mt-6">
        Já tem conta?{' '}
        <Link to="/login" className="text-institutional font-semibold hover:underline">
          Entrar
        </Link>
      </p>
    </AuthShell>
  );
}
